import { v4 as uuidV4 } from 'uuid';
import { differenceInMilliseconds } from 'date-fns';
import { parseAbsolute } from '@internationalized/date';
import { intersectionBy, keyBy, mergeWith, pick, uniqBy } from 'lodash-es';
import {
  AccommodationLeg,
  AircraftLegResource,
  AirportLegLocationReference,
  AviaLegTypes,
  BaseLegResourceType,
  CustomLocationType,
  FacilityLegLocationReference,
  GroundLegTypes,
  LegLocationReferenceTypeEnum,
  LegLocationType,
  LegLocationTypeEnum,
  LogisticsLegResourceType,
  RouteLegTypeEnum,
  RouteLocationType,
  RouteTypeEnum,
  SegmentTypeEnum,
} from '@/api/apis/route';
import {
  ActionTypeEnum,
  FlightTypeEnum,
  PlanType,
  RouteActionsType,
  RoutePlanAircraft,
  SurgeonTrip,
  SurgeonTripPartStatus,
  TaskAction,
  PlanTeam,
  TaskLocation,
  PlanActionTeamKeyword,
  TeamResourcesByKeyword,
  RouteTypeSettingsEnum,
} from '@/api/apis/mission-planner';
import { CaseFacility, CaseResourceType } from '@/api/apis/case';
import { queryClient } from '@/queries';
import { geoQueries } from '@/queries/geo-library';
import { CaseLocationIds, fetchLocationsList } from '@/queries/route-planner/utils';
import { isGroundLegType, OPERATION_AT_TX_CENTER_DURATION } from '@/utils/route/config';
import { RoutePlanResources, RoutePlanType, RouteSegmentType, SurgeonTripRoutePlan } from '@/types/route';
import {
  getSegmentMapKey,
  convertTaskActionToRouteLeg,
  getFixedTaskLocationType,
  ConvertTaskActionToRouteLegParams,
  generateTmdxDriveTedsRouteLegs,
  getCustomLocationId,
  sortSuggestedSegments,
} from '@/utils/route';
import { convertHereLocation } from '@/utils/helpers';

const getLocationType = (taskLocation: TaskLocation): LegLocationType => {
  const fixedLocation = getFixedTaskLocationType(taskLocation);
  const isCustom = fixedLocation.type === LegLocationTypeEnum.custom;

  return {
    ...fixedLocation,
    ...(isCustom ? { data: { location: taskLocation.location } } : {}),
  } as LegLocationType;
};

const SEGMENT_TYPE_MAP: Record<string, SegmentTypeEnum> = {
  Hub: SegmentTypeEnum.hospital,
  Hospital: SegmentTypeEnum.txcenter,
  TxCenter: SegmentTypeEnum.hub,
};

const END_LOCATION_TYPE_MAP: Record<string, SegmentTypeEnum> = {
  Hospital: SegmentTypeEnum.hospital,
  TxCenter: SegmentTypeEnum.txcenter,
  Hub: SegmentTypeEnum.hub,
};

const MAIN_SEGMENTS_LIST = Object.values(SEGMENT_TYPE_MAP);

// Only the case DH/TXC (segments' end locations) switch main segments, other DH/TXC are regular stops
export const getPrimaryLocationIdByType = (segments: RouteSegmentType[]): Record<string, string | undefined> => ({
  Hospital: segments.find((s) => s.type === SegmentTypeEnum.hospital)?.endLocation.locationId,
  TxCenter: segments.find((s) => s.type === SegmentTypeEnum.txcenter)?.endLocation.locationId,
});

export const isMainSegmentLocation = (
  taskLocation: TaskLocation,
  primaryLocationIdByType: Record<string, string | undefined>,
  mainLocationTypes: string[],
) => {
  const { abstractLocationType } = taskLocation;

  if (!mainLocationTypes.includes(abstractLocationType)) return false;
  if (!(abstractLocationType in primaryLocationIdByType)) return true;

  const primaryId = primaryLocationIdByType[abstractLocationType];

  return !primaryId || getFixedTaskLocationType(taskLocation).id === primaryId;
};

const getRouteLegFromAction = async (
  data: ConvertTaskActionToRouteLegParams,
): Promise<AviaLegTypes | GroundLegTypes> => {
  const leg = await convertTaskActionToRouteLeg(data);

  return leg as AviaLegTypes | GroundLegTypes;
};

type SetActionToMapParams = {
  action: TaskAction;
  segmentType: SegmentTypeEnum;
  segmentId: string;
  map: Map<string, (AviaLegTypes | GroundLegTypes)[]>;
  teamResources: Partial<TeamResourcesByKeyword>;
  isTmdxDriveCompatible: boolean;
};

const setActionToMap = async ({
  action,
  segmentType,
  segmentId,
  map,
  teamResources,
  isTmdxDriveCompatible,
}: SetActionToMapParams) => {
  const segmentMapKey = `${segmentType}|${segmentId}`;
  const legs = map.get(segmentMapKey) || [];
  const routeLeg = await getRouteLegFromAction({
    action,
    currentSegment: segmentType,
    legs,
    teamResources,
    isTmdxDriveCompatible,
  });

  if (action.actionType === ActionTypeEnum.DriveAction || action.actionType === ActionTypeEnum.CommercialFlightAction) {
    map.set(segmentMapKey, [...legs, routeLeg]);
  }

  if (action.actionType === ActionTypeEnum.FlyAction) {
    switch (action.flightType) {
      case FlightTypeEnum.DIRECT:
      case FlightTypeEnum.DIRECT_VIA_MIDDLE:
        map.set(segmentMapKey, [...legs, routeLeg]);
        break;
      default:
        break;
    }
  }
};

const convertMainDryRunActions = async (
  actions: TaskAction[],
  segments: RouteSegmentType[],
  teamResources: Partial<TeamResourcesByKeyword>,
  isTmdxDriveCompatible: boolean,
): Promise<Map<string, (AviaLegTypes | GroundLegTypes)[]>> => {
  const routesMap = new Map() as Map<string, (AviaLegTypes | GroundLegTypes)[]>;
  const segment = segments.find((s) => s.type === SegmentTypeEnum.dhToHub);

  for await (const action of actions) {
    await setActionToMap({
      action,
      segmentType: SegmentTypeEnum.dhToHub,
      segmentId: segment?.id || SegmentTypeEnum.dhToHub,
      map: routesMap,
      teamResources,
      isTmdxDriveCompatible,
    });
  }

  return routesMap;
};

const convertCustomActions = async (
  actions: TaskAction[],
  teamResources: Partial<TeamResourcesByKeyword>,
  isTmdxDriveCompatible: boolean,
): Promise<Pick<RoutePlanType, 'segments' | 'legs'>> => {
  const customActions = actions.filter((a) => a.teamKeyword !== PlanActionTeamKeyword.team);
  const routesMap = new Map() as Map<string, (AviaLegTypes | GroundLegTypes)[]>;
  const segmentIds: string[] = [];
  let prevActionIndex = -1;
  let customSegmentId = '';

  for await (const action of customActions) {
    const legIndex = actions.findIndex((a) => a.id === action.id);

    if (!customSegmentId || legIndex - prevActionIndex > 1) {
      customSegmentId = uuidV4();
      segmentIds.push(customSegmentId);
    }

    prevActionIndex = legIndex;

    await setActionToMap({
      action,
      segmentType: SegmentTypeEnum.custom,
      segmentId: customSegmentId,
      map: routesMap,
      teamResources,
      isTmdxDriveCompatible,
    });
  }

  const segments = segmentIds.map((id) => {
    const legs = routesMap.get(`${SegmentTypeEnum.custom}|${id}`) || [];

    const firstLeg = legs[0];
    const lastLeg = legs[legs.length - 1];

    const isFirstGround = isGroundLegType(firstLeg);
    const isLastGround = isGroundLegType(lastLeg);

    return {
      id,
      startLocation: isFirstGround
        ? firstLeg.departureLocation.locationReference || firstLeg.departureLocation.airportReference
        : firstLeg.departureLocation,
      endLocation: isLastGround
        ? lastLeg.arrivalLocation.locationReference || lastLeg.arrivalLocation.airportReference
        : lastLeg.arrivalLocation,
      startTime: firstLeg.departureTime,
      endTime: lastLeg.arrivalTime,
      type: SegmentTypeEnum.custom,
    } as RouteSegmentType;
  });

  return {
    segments,
    legs: routesMap,
  };
};

const convertMainActions = async (
  actions: TaskAction[],
  segments: RouteSegmentType[],
  teamResources: Partial<TeamResourcesByKeyword>,
  isTmdxDriveCompatible: boolean,
): Promise<Map<string, (AviaLegTypes | GroundLegTypes)[]>> => {
  const routesMap = new Map() as Map<string, (AviaLegTypes | GroundLegTypes)[]>;
  const primaryLocationIdByType = getPrimaryLocationIdByType(segments);

  const firstMainAction = actions.find((a) =>
    isMainSegmentLocation(a.to, primaryLocationIdByType, Object.keys(END_LOCATION_TYPE_MAP)),
  );
  let currentSegmentType = firstMainAction
    ? END_LOCATION_TYPE_MAP[firstMainAction.to.abstractLocationType]
    : SegmentTypeEnum.hospital;
  let currentSegment = segments.find((s) => s.type === currentSegmentType);

  for await (const [index, action] of actions.entries()) {
    if (index && isMainSegmentLocation(action.from, primaryLocationIdByType, Object.keys(SEGMENT_TYPE_MAP))) {
      const nextSegmentType = SEGMENT_TYPE_MAP[action.from.abstractLocationType];
      const currentSegmentTypeIndex = MAIN_SEGMENTS_LIST.indexOf(currentSegmentType);
      const nextSegmentTypeIndex = MAIN_SEGMENTS_LIST.indexOf(nextSegmentType);

      if (nextSegmentTypeIndex > currentSegmentTypeIndex) {
        currentSegmentType = nextSegmentType;
        currentSegment = segments.find((s) => s.type === nextSegmentType);
      }
    }

    if (action.teamKeyword === PlanActionTeamKeyword.team) {
      const segmentType = currentSegmentType;
      const segmentId = currentSegment?.id || currentSegmentType;
      await setActionToMap({
        action,
        segmentType,
        segmentId,
        map: routesMap,
        teamResources,
        isTmdxDriveCompatible,
      });
    }
  }

  return routesMap;
};

const convertRepositionActions = async (
  segments: RouteSegmentType[],
  actions: TaskAction[],
  mainLegsMap: Map<string, (AviaLegTypes | GroundLegTypes)[]>,
): Promise<{ segments: RouteSegmentType[]; legs: Map<string, (AviaLegTypes | GroundLegTypes)[]> }> => {
  const mainSegments = [...segments];
  const legsMap = new Map() as Map<string, (AviaLegTypes | GroundLegTypes)[]>;
  const legsList = [...mainLegsMap.values()].flat();

  for await (const action of actions) {
    const isReposition = action.flightType === FlightTypeEnum.REPOSITION;
    const parentAction = legsList.find((r) => r.id === action.parentId);
    const parentSegmentType = parentAction?.segmentType;

    if (parentSegmentType) {
      const repositionType = isReposition ? SegmentTypeEnum.reposition : SegmentTypeEnum.returnToBase;
      const routeLeg = await getRouteLegFromAction({
        action,
        currentSegment: repositionType,
        legs: [],
        teamResources: {},
      });
      const currentSegment = mainSegments.find((s) => {
        const isRepositionSegment = s.type === repositionType;
        const isCurrentLocation = s.endLocation.id === routeLeg.departureLocation.id;
        return isRepositionSegment && isCurrentLocation;
      });

      const currentSegmentLegs = currentSegment ? legsMap.get(getSegmentMapKey(currentSegment)) || [] : [];

      if (!currentSegment) {
        const parentSegmentIndex = mainSegments.findIndex((s) => s.type === parentSegmentType);
        const newSegmentPosition = isReposition ? parentSegmentIndex : parentSegmentIndex + 1;
        const newSegment = {
          id: uuidV4(),
          startLocation: routeLeg.departureLocation,
          endLocation: routeLeg.arrivalLocation,
          type: repositionType,
        } as RouteSegmentType;

        mainSegments.splice(newSegmentPosition, 0, newSegment);

        legsMap.set(getSegmentMapKey(newSegment), [routeLeg]);
      } else {
        const currentSegmentIndex = mainSegments.findIndex((s) => s.id === currentSegment.id);
        const updatedSegment = {
          ...currentSegment,
          endLocation: routeLeg.arrivalLocation,
        } as RouteSegmentType;

        mainSegments.splice(currentSegmentIndex, 1, updatedSegment);
        legsMap.set(getSegmentMapKey(currentSegment), [
          ...currentSegmentLegs,
          {
            ...routeLeg,
            chainPosition: {
              ...routeLeg.chainPosition,
              groupId: currentSegmentLegs[currentSegmentLegs.length - 1].chainPosition.groupId,
              previousLegId: currentSegmentLegs[currentSegmentLegs.length - 1].id,
            },
          },
        ]);
      }
    }
  }

  return { segments: mainSegments, legs: legsMap };
};

const getResourceByType = (
  resource: PlanTeam['specialist'] | PlanTeam['ocs'] | PlanTeam['surgeon'] | undefined,
  type: CaseResourceType,
  hasResource: boolean,
): Partial<RoutePlanResources> => {
  if (hasResource) {
    return {};
  }

  const isOcsDevice = type === CaseResourceType.OCSConsole;

  return {
    ...(resource
      ? {
          [type]: {
            resourceId: resource.id,
            resourceType: type,
            fallbackInfo: isOcsDevice ? null : resource,
          },
        }
      : {}),
  };
};

const getResources = (
  teamResources: Partial<TeamResourcesByKeyword>,
  actions: TaskAction[],
): Partial<RoutePlanResources> => {
  const clinicalResources = Object.keys(teamResources).reduce<Partial<RoutePlanResources>>((acc, teamKeyword) => {
    switch (teamKeyword) {
      case PlanActionTeamKeyword.surgeonOnly:
        const surgeonResource = getResourceByType(
          teamResources[teamKeyword]?.surgeon,
          CaseResourceType.Surgeon,
          !!acc[CaseResourceType.Surgeon],
        );

        return {
          ...acc,
          ...surgeonResource,
        };
      case PlanActionTeamKeyword.team:
        const teamSpecialist = getResourceByType(
          teamResources[teamKeyword]?.specialist,
          CaseResourceType.ClinicalSpecialist,
          !!acc[CaseResourceType.ClinicalSpecialist],
        );
        const deviceResource = getResourceByType(
          teamResources[teamKeyword]?.ocs,
          CaseResourceType.OCSConsole,
          !!acc[CaseResourceType.OCSConsole],
        );

        return {
          ...acc,
          ...teamSpecialist,
          ...deviceResource,
        };

      case PlanActionTeamKeyword.teamWithoutOcs:
        const teamWithoutOcsSpecialist = getResourceByType(
          teamResources[teamKeyword]?.specialist,
          CaseResourceType.ClinicalSpecialist,
          !!acc[CaseResourceType.ClinicalSpecialist],
        );

        return {
          ...acc,
          ...teamWithoutOcsSpecialist,
        };
      default:
        return acc;
    }
  }, {} as Partial<RoutePlanResources>);

  const actionsWithAircraft = actions.filter((a) => !!a.aircraftInfo);
  const aircrafts = uniqBy(
    actionsWithAircraft.map<RoutePlanAircraft>((a) => a.aircraftInfo!),
    'id',
  );

  const aircraftResources = aircrafts.map((aircraft) => ({
    id: aircraft.id,
    type: CaseResourceType.Aircraft,
    data: aircraft,
  })) as BaseLegResourceType<AircraftLegResource>[];

  return { ...clinicalResources, [CaseResourceType.Aircraft]: aircraftResources };
};

type GetDeviceDeliveryActionParams = {
  txCenter?: CaseFacility;
  currentSegments: RouteSegmentType[];
  currentRouteMap: Map<string, (AviaLegTypes | GroundLegTypes)[]>;
  isTmdxDriveCompatible: boolean;
  deviceResource?: LogisticsLegResourceType;
  planType: RouteTypeSettingsEnum;
};

const getDeviceDeliveryAction = async ({
  txCenter,
  currentSegments,
  currentRouteMap,
  isTmdxDriveCompatible,
  deviceResource,
  planType,
}: GetDeviceDeliveryActionParams): Promise<Pick<RoutePlanType, 'segments' | 'legs'>> => {
  const isHubDH = planType === RouteTypeSettingsEnum.Hub_DH;
  if (!txCenter || !deviceResource || isHubDH) {
    return {
      segments: currentSegments,
      legs: currentRouteMap,
    };
  }

  const txToHubReturnSegment = currentSegments.find((s) => s.type === SegmentTypeEnum.hub);
  const dhToTxReturnSegment = currentSegments.find((s) => s.type === SegmentTypeEnum.txcenter);
  const hasSegments = txToHubReturnSegment && dhToTxReturnSegment;
  const hasTxToHubLegs = !!currentRouteMap.get(getSegmentMapKey(txToHubReturnSegment!))?.length;

  if (!isTmdxDriveCompatible || !hasSegments || hasTxToHubLegs) {
    return {
      segments: currentSegments,
      legs: currentRouteMap,
    };
  }

  const txCenterDepartureTime = parseAbsolute(dhToTxReturnSegment.endTime!, 'UTC')
    .add({ milliseconds: OPERATION_AT_TX_CENTER_DURATION })
    .toString();

  const tmdxDriveTedsLeg = await generateTmdxDriveTedsRouteLegs({
    txCenter,
    hubId: txToHubReturnSegment.endLocation.locationId,
    departureTime: txCenterDepartureTime,
    deviceResource,
  });

  const updatedSegments = currentSegments.map((s) => {
    if (s.type !== SegmentTypeEnum.hub) return s;

    return {
      ...s,
      startTime: tmdxDriveTedsLeg.departureTime,
      endTime: tmdxDriveTedsLeg.arrivalTime,
    };
  });

  currentRouteMap.set(getSegmentMapKey(txToHubReturnSegment!), [tmdxDriveTedsLeg]);

  return {
    segments: updatedSegments,
    legs: currentRouteMap,
  };
};

export const convertPlanActions = async (
  segments: RouteSegmentType[],
  actions: TaskAction[],
  teamResources: Partial<TeamResourcesByKeyword>,
  isTmdxDriveCompatible: boolean,
  type: RouteTypeEnum = RouteTypeEnum.Main,
): Promise<{
  segments: RouteSegmentType[];
  legs: Map<string, (AviaLegTypes | GroundLegTypes)[]>;
  resources: Partial<RoutePlanResources>;
  isValid: boolean;
}> => {
  const plannedHubFrom = actions.find((a) => a.from.abstractLocationType === 'Hub');
  const plannedHubTo = actions.find((a) => a.to.abstractLocationType === 'Hub');

  const currentSegments = [...segments].map((s) => {
    if (plannedHubFrom && s.startLocation.locationType === LegLocationTypeEnum.hub) {
      return {
        ...s,
        startLocation: {
          $type: LegLocationReferenceTypeEnum.facility,
          locationType: LegLocationTypeEnum.hub,
          id: plannedHubFrom.from.id,
          locationId: plannedHubFrom.from.id,
        } as FacilityLegLocationReference,
      };
    }

    if (plannedHubTo && s.endLocation.locationType === LegLocationTypeEnum.hub) {
      return {
        ...s,
        endLocation: {
          $type: LegLocationReferenceTypeEnum.facility,
          locationType: LegLocationTypeEnum.hub,
          id: plannedHubTo.to.id,
          locationId: plannedHubTo.to.id,
        } as FacilityLegLocationReference,
      };
    }

    return s;
  });

  const childActions = actions.filter((a) => !!a.parentId);
  const mainActions = actions.filter((a) => !a.parentId);

  const mainRoutesMap =
    type === RouteTypeEnum.Main
      ? await convertMainActions(mainActions, currentSegments, teamResources, isTmdxDriveCompatible)
      : await convertMainDryRunActions(mainActions, currentSegments, teamResources, isTmdxDriveCompatible);
  const customSegments = await convertCustomActions(mainActions, teamResources, isTmdxDriveCompatible);
  const fullRouteMap = new Map([...mainRoutesMap, ...customSegments.legs]);
  const resources = getResources(teamResources, actions);

  const { segments: newSegments, legs } = await convertRepositionActions(
    [...currentSegments, ...customSegments.segments],
    childActions,
    mainRoutesMap,
  );

  const legsMap = new Map([...fullRouteMap, ...legs]);
  const isValidLegs = [...legsMap.values()]
    .flat()
    .flatMap((a) => {
      const isGroundLeg = isGroundLegType(a);
      let airports: AirportLegLocationReference[] = [];
      const isCustomSegment = a.segmentType === SegmentTypeEnum.custom;

      if (isCustomSegment) {
        return airports;
      }

      if (a.departureLocation.locationType === LegLocationTypeEnum.airport) {
        const departureAirport = isGroundLeg ? a.departureLocation.airportReference : a.departureLocation;
        airports = [...airports, departureAirport];
      }

      if (a.arrivalLocation.locationType === LegLocationTypeEnum.airport) {
        const arrivalAirport = isGroundLeg ? a.arrivalLocation.airportReference : a.arrivalLocation;
        airports = [...airports, arrivalAirport];
      }

      return airports;
    })
    .every((a) => !!a.fboReference?.fboAddressId);

  return { segments: newSegments, legs: legsMap, resources, isValid: isValidLegs };
};

const convertAccommodationLegs = async (
  segments: RouteSegmentType[],
  actions: TaskAction[],
  teamResources: Partial<TeamResourcesByKeyword>,
): Promise<{ segments: RouteSegmentType[]; accommodationLegs: Map<string, AccommodationLeg[]> }> => {
  const mainSegments = [...segments];
  const accommodationLegs: Map<string, AccommodationLeg[]> = new Map();

  for await (const action of actions) {
    const currentLeg = (await convertTaskActionToRouteLeg({
      action,
      currentSegment: SegmentTypeEnum.custom,
      legs: [],
      teamResources,
    })) as AccommodationLeg;

    const newSegment = {
      id: uuidV4(),
      startLocation: currentLeg.location,
      type: currentLeg.segmentType,
      startTime: currentLeg.checkInAt,
      minDate: currentLeg.checkInAt,
      endLocation: currentLeg.location,
      isAccommodation: true,
    } as RouteSegmentType;

    mainSegments.push(newSegment);

    accommodationLegs.set(getSegmentMapKey(newSegment), [currentLeg]);
  }

  return { segments: mainSegments, accommodationLegs };
};

const setIdToActions = (actions: TaskAction[]): TaskAction[] => {
  return actions.map((action) => ({
    ...action,
    id: uuidV4(),
    subActions: setIdToActions(action.subActions || []),
  }));
};

const convertActionsToFlatArray = (actions: TaskAction[], parentId?: string): TaskAction[] => {
  const mainActions = actions.filter((action) => {
    return [
      ActionTypeEnum.DriveAction,
      ActionTypeEnum.FlyAction,
      ActionTypeEnum.CommercialFlightAction,
      ActionTypeEnum.StayOvernightAction,
    ].includes(action.actionType);
  });

  const repositionActions = actions
    .flatMap((action) => action.subActions || [])
    .reduce((acc, action, index, array) => {
      if (![ActionTypeEnum.FlyAction, ActionTypeEnum.StayOvernightAction].includes(action.actionType)) return acc;

      if (action.actionType === ActionTypeEnum.FlyAction) {
        const blockBeforeAction = array[index - 1];
        const blockAfterAction = array[index + 1];

        return [
          ...acc,
          {
            ...action,
            startTime:
              blockBeforeAction?.actionType === ActionTypeEnum.BlockAction
                ? blockBeforeAction.startTime
                : action.startTime,
            endTime:
              blockAfterAction?.actionType === ActionTypeEnum.BlockAction ? blockAfterAction.endTime : action.endTime,
          },
        ];
      }

      return [...acc, action];
    }, [] as TaskAction[]);

  repositionActions.forEach((action) => {
    if ([ActionTypeEnum.FlyAction, ActionTypeEnum.StayOvernightAction].includes(action.actionType)) {
      const findMethod = action.flightType === FlightTypeEnum.REPOSITION ? 'find' : 'findLast';
      const currentFlight = mainActions[findMethod]((a) => a.aircraftInfo?.id === action.aircraftInfo?.id);

      if (currentFlight) {
        currentFlight.subActions = [...(currentFlight.subActions || []), action];
      }
    }
  });

  return mainActions
    .reduce<TaskAction[]>((acc, action) => {
      const allSubActions = action.subActions || [];

      let { startTime } = action;
      let { endTime } = action;

      if (action.actionType === ActionTypeEnum.FlyAction) {
        const currentFlightIndex = actions.findIndex(
          (a) => a.actionType === action.actionType && a.startTime === action.startTime && a.endTime === action.endTime,
        );

        const blockBeforeAction = actions[currentFlightIndex - 1];
        const blockAfterAction = actions[currentFlightIndex + 1];

        startTime =
          blockBeforeAction?.actionType === ActionTypeEnum.BlockAction ? blockBeforeAction.startTime : startTime;
        endTime = blockAfterAction?.actionType === ActionTypeEnum.BlockAction ? blockAfterAction.endTime : endTime;
      }

      if (allSubActions.length) {
        return [
          ...acc,
          {
            ...action,
            startTime,
            endTime,
            subActions: [],
            parentId,
          },
          ...convertActionsToFlatArray(allSubActions, action.id),
        ];
      }

      return [...acc, { ...action, parentId, startTime, endTime }];
    }, [] as TaskAction[])
    .sort((a: TaskAction, b: TaskAction) => {
      return differenceInMilliseconds(new Date(a.startTime), new Date(b.startTime)) > 0 ? 1 : -1;
    });
};

export const convertActionsToSortedFlatArray = (actions: TaskAction[]): TaskAction[] => {
  const idActions = setIdToActions(actions);

  return convertActionsToFlatArray(idActions);
};

export const convertDryRunActionsToSortedFlatArray = (actions: TaskAction[] = []): TaskAction[] => {
  const operationAtHospitalIndex = actions.findIndex((a) => a.actionType === ActionTypeEnum.OperationAtHospitalAction);

  if (operationAtHospitalIndex < 0) return [];

  const dryRunActions = actions.slice(operationAtHospitalIndex);
  const idActions = setIdToActions(dryRunActions);

  return convertActionsToFlatArray(idActions);
};

const fetchPlanLocations = async (
  actions: TaskAction[],
  caseLocationIds?: CaseLocationIds,
): Promise<RouteLocationType[]> => {
  const locationsArray = actions.map((action) => ({
    startLocation: getLocationType(action.from),
    endLocation: getLocationType(action.to),
  }));

  const [fetchedLocations, geoLibrary] = await Promise.all([
    fetchLocationsList(locationsArray, caseLocationIds),
    queryClient.ensureQueryData(geoQueries.registry()),
  ]);
  const countriesByIso2 = keyBy(geoLibrary.countries, 'alpha2');

  return fetchedLocations.map((location) => {
    switch (location.type) {
      case LegLocationTypeEnum.hub:
        return {
          id: location.data.id,
          locationType: LegLocationTypeEnum.hub,
          location: location.data.location as CustomLocationType,
          name: location.data.name,
          code: location.data.code,
          logoPath: location.data.image,
        };
      case LegLocationTypeEnum.homeResidence:
        return {
          id: location.data.id,
          locationType: LegLocationTypeEnum.homeResidence,
          location: location.data as CustomLocationType,
        };
      case LegLocationTypeEnum.custom:
        return {
          id: getCustomLocationId(location.data.id),
          locationType: LegLocationTypeEnum.custom,
          location: convertHereLocation(location.data, countriesByIso2),
          hereMapsId: location.data.id,
        };
      case LegLocationTypeEnum.txcenter:
        return {
          id: location.data.id,
          locationType: LegLocationTypeEnum.txcenter,
          location: location.data.location as CustomLocationType,
          name: location.data.name,
          code: location.data.code,
          logoPath: location.data.image,
        };
      case LegLocationTypeEnum.hospital:
        return {
          id: location.data.id,
          locationType: LegLocationTypeEnum.hospital,
          location: location.data.location as CustomLocationType,
          name: location.data.name,
          code: location.data.code ?? '',
          logoPath: location.data.logoPath,
        };
      case LegLocationTypeEnum.opo:
        return {
          id: location.data.id,
          locationType: LegLocationTypeEnum.opo,
          location: location.data.location as CustomLocationType,
          name: location.data.name,
          code: location.data.code,
        };
      case LegLocationTypeEnum.airport:
      default:
        return {
          ...location.data,
          fboLocations: location.data.fbos,
          location: location.data.location as CustomLocationType,
          locationType: LegLocationTypeEnum.airport,
        };
    }
  });
};

export const getCaseRouteLocations = ({
  donorHospital,
  txCenter,
  opo,
}: {
  donorHospital?: CaseFacility | null;
  txCenter?: CaseFacility | null;
  opo?: CaseFacility | null;
}): RouteLocationType[] => [
  ...(donorHospital
    ? [
        {
          id: donorHospital.id,
          locationType: LegLocationTypeEnum.hospital as const,
          name: donorHospital.name,
          code: donorHospital.code,
          logoPath: donorHospital.logoPath,
          location: donorHospital.location,
        },
      ]
    : []),
  ...(txCenter
    ? [
        {
          id: txCenter.id,
          locationType: LegLocationTypeEnum.txcenter as const,
          name: txCenter.name,
          code: txCenter.code,
          logoPath: txCenter.logoPath,
          location: txCenter.location,
        },
      ]
    : []),
  ...(opo
    ? [
        {
          id: opo.id,
          locationType: LegLocationTypeEnum.opo as const,
          name: opo.name,
          code: opo.code,
          location: opo.location,
        },
      ]
    : []),
];

type GetRoutePlanParams = {
  txCenter?: CaseFacility;
  donorHospital?: CaseFacility;
  plan: PlanType;
  defaultSegments: RouteSegmentType[];
  dryRunDefaultSegments: RouteSegmentType[];
  planType: RouteTypeSettingsEnum;
  isTmdxDriveCompatible: boolean;
};

export const getRoutePlan = async ({
  txCenter,
  donorHospital,
  plan,
  defaultSegments,
  dryRunDefaultSegments,
  planType,
  isTmdxDriveCompatible,
}: GetRoutePlanParams): Promise<RoutePlanType> => {
  const planActionsSorted = convertActionsToSortedFlatArray(plan.actions);
  const dryRunActionsSorted = convertDryRunActionsToSortedFlatArray(plan.dryRunActions);
  const routeActions = planActionsSorted.filter((action) => action.actionType !== ActionTypeEnum.StayOvernightAction);
  const routeAccommodationActions = planActionsSorted.filter(
    (action) => action.actionType === ActionTypeEnum.StayOvernightAction,
  );

  const dryRunRouteActions = dryRunActionsSorted.filter(
    (action) => action.actionType !== ActionTypeEnum.StayOvernightAction,
  );
  const dryRunRouteAccommodationActions = dryRunActionsSorted.filter(
    (action) => action.actionType === ActionTypeEnum.StayOvernightAction,
  );

  const fetchedLocations = await fetchPlanLocations([...routeActions, ...dryRunRouteActions], {
    txCenterId: txCenter?.id,
    donorHospitalId: donorHospital?.id,
  });
  const locations = uniqBy([...getCaseRouteLocations({ donorHospital, txCenter }), ...fetchedLocations], 'id');

  const teamResources = pick(plan, [PlanActionTeamKeyword.team, PlanActionTeamKeyword.teamWithoutOcs]);

  const {
    segments: mainRouteSegments,
    legs: mainRouteLegs,
    resources: mainResources,
    isValid: isMainRouteValid,
  } = await convertPlanActions(defaultSegments, routeActions, teamResources, isTmdxDriveCompatible);
  const {
    segments: updatedMainRouteSegments,
    accommodationLegs: mainAccommodationLegs,
  }: {
    segments: RouteSegmentType[];
    accommodationLegs: Map<string, AccommodationLeg[]>;
  } = await convertAccommodationLegs(mainRouteSegments, routeAccommodationActions, teamResources);

  const {
    segments: dryRunRouteSegments,
    legs: dryRunRouteLegs,
    resources: dryRunResources,
    isValid: isDryRunRouteValid,
  } = await convertPlanActions(
    dryRunDefaultSegments,
    dryRunRouteActions,
    teamResources,
    isTmdxDriveCompatible,
    RouteTypeEnum.DryRun,
  );
  const { segments: sortedDryRunRouteSegments, accommodationLegs: dryRunAccommodationLegs } =
    await convertAccommodationLegs(dryRunRouteSegments, dryRunRouteAccommodationActions, teamResources);

  const mappedMainRouteSegments = updatedMainRouteSegments.map((s) => {
    const routeLegs = mainRouteLegs.get(getSegmentMapKey(s)) || [];

    if (!routeLegs.length) {
      return s;
    }

    const startLeg = routeLegs[0];
    const endLeg = routeLegs[routeLegs.length - 1];

    return {
      ...s,
      startTime: startLeg.departureTime,
      endTime: endLeg.arrivalTime,
    };
  });

  const { segments: mainSegments, legs: mainLegs } = await getDeviceDeliveryAction({
    currentSegments: mappedMainRouteSegments,
    currentRouteMap: mainRouteLegs,
    txCenter,
    isTmdxDriveCompatible,
    deviceResource: mainResources[CaseResourceType.OCSConsole],
    planType,
  });

  const hasFakeCommercialFlights = [...mainLegs.values()]
    .flat()
    .some((l) => l.legType === RouteLegTypeEnum.CommercialFlightAviaLeg);

  return {
    id: uuidV4(),
    segments: sortSuggestedSegments(mainSegments),
    legs: mainLegs,
    accommodationLegs: mainAccommodationLegs,
    metrics: plan.planMetrics,
    dryRunSegments: sortedDryRunRouteSegments,
    dryRunLegs: dryRunRouteLegs,
    dryRunAccommodationLegs,
    dryRunMetrics: plan.dryRunMetrics,
    resources: {
      ...mainResources,
      [CaseResourceType.Aircraft]: uniqBy(
        [
          ...(mainResources?.[CaseResourceType.Aircraft] || []),
          ...(dryRunResources?.[CaseResourceType.Aircraft] || []),
        ],
        'id',
      ),
    },
    teamResources,
    locations,
    hasFakeCommercialFlights,
    isValid: isMainRouteValid && (!dryRunRouteActions.length || isDryRunRouteValid),
  };
};

export const convertSurgeonTrips = async (
  trip: SurgeonTrip,
  isTmdxDriveCompatible: boolean,
): Promise<SurgeonTripRoutePlan | null> => {
  if (!trip.surgeon.id || trip.fromHome.status !== SurgeonTripPartStatus.Found) return null;

  const isTripHomeMissing = trip.toHome.status !== SurgeonTripPartStatus.Found;

  const teamResources = {
    [PlanActionTeamKeyword.surgeonOnly]: {
      surgeon: trip.surgeon,
    },
  } as Pick<TeamResourcesByKeyword, PlanActionTeamKeyword.surgeonOnly>;

  const fromHomeActions = trip.fromHome.actions.map(
    (a) =>
      ({
        ...a,
        teamKeyword: PlanActionTeamKeyword.surgeonOnly,
      }) as TaskAction,
  );

  const toHomeActions = (isTripHomeMissing ? [] : trip.toHome.actions).map(
    (a) =>
      ({
        ...a,
        teamKeyword: PlanActionTeamKeyword.surgeonOnly,
      }) as TaskAction,
  );

  const toDHActions = convertActionsToSortedFlatArray(fromHomeActions);
  const fromDHActions = convertActionsToSortedFlatArray(toHomeActions);
  const actionsList = [...toDHActions, ...fromDHActions];

  const locations = await fetchPlanLocations(actionsList);

  const start = await convertCustomActions(toDHActions, teamResources, isTmdxDriveCompatible);
  const end = await convertCustomActions(fromDHActions, teamResources, isTmdxDriveCompatible);

  const legsMap = new Map([...start.legs, ...end.legs]);

  return {
    segments: [...start.segments, ...end.segments],
    legs: legsMap,
    resources: getResources(teamResources, actionsList),
    locations,
    isTripHomeMissing,
  };
};

export const mergeSurgeonAndMainRoute = (
  mainRoute: RoutePlanType,
  surgeonRoute: Pick<RoutePlanType, 'segments' | 'legs' | 'locations' | 'resources'>,
): Pick<RoutePlanType, 'segments' | 'legs' | 'locations' | 'resources'> => {
  const surgeonDisconnectedIndices = surgeonRoute.segments.reduce<number[]>((acc, s, index, array) => {
    const prevSegment = array[index - 1];
    if (!index || !prevSegment) return acc;

    const currentStartLocation = s.startLocation.id;
    const prevEndLocation = prevSegment.endLocation.id;

    return currentStartLocation === prevEndLocation ? acc : [...acc, index];
  }, []);

  const mergedResources = mergeWith(mainRoute.resources, surgeonRoute.resources, (left, right) => {
    if (Array.isArray(left)) {
      return left.concat(right);
    }

    return undefined;
  });

  if (!surgeonDisconnectedIndices.length) {
    const legsMap = new Map([...mainRoute.legs, ...surgeonRoute.legs]);

    return {
      segments: [...mainRoute.segments, ...surgeonRoute.segments],
      legs: legsMap,
      resources: mergedResources,
      locations: uniqBy([...mainRoute.locations, ...surgeonRoute.locations], 'id'),
    };
  }

  let sortedLegs = [...mainRoute.legs.values()]
    .flat()
    .sort((a, b) => (differenceInMilliseconds(a.departureTime, b.arrivalTime) > 0 ? 1 : -1));

  surgeonDisconnectedIndices.forEach((index) => {
    const prevSegment = surgeonRoute.segments[index - 1];
    const currentSegment = surgeonRoute.segments[index];

    const startIndex = sortedLegs.findIndex(
      (l) =>
        l.departureLocation.locationId === prevSegment.endLocation.id &&
        l.resources.some((r) => r.resourceType === CaseResourceType.OCSConsole),
    );

    const endIndex = sortedLegs.findLastIndex(
      (l) =>
        l.arrivalLocation.locationId === currentSegment.startLocation.id &&
        l.resources.some((r) => r.resourceType === CaseResourceType.OCSConsole),
    );

    sortedLegs = sortedLegs.map((l, legIndex) => {
      if (legIndex >= startIndex && legIndex <= endIndex) {
        const hasResource = l.resources.some((r) => r.resourceType === CaseResourceType.OCSConsole);
        const hasSurgeon = l.resources.some((r) => r.resourceType === CaseResourceType.Surgeon);

        if (hasResource && !hasSurgeon) {
          return {
            ...l,
            resources: [...l.resources, ...(surgeonRoute.resources?.Surgeon ? [surgeonRoute.resources?.Surgeon] : [])],
          };
        }
      }

      return l;
    });
  });

  const newMainLegsMap = new Map<string, (AviaLegTypes | GroundLegTypes)[]>();

  mainRoute.segments.forEach((segment) => {
    const segmentMapKey = getSegmentMapKey(segment);
    const origLegs = mainRoute.legs.get(segmentMapKey) || [];

    newMainLegsMap.set(
      segmentMapKey,
      origLegs
        .map((l) => sortedLegs.find((sl) => sl.id === l.id))
        .filter((s): s is AviaLegTypes | GroundLegTypes => !!s) || [],
    );
  });

  const legsMap = new Map([...newMainLegsMap, ...surgeonRoute.legs]);

  return {
    segments: sortSuggestedSegments([...mainRoute.segments, ...surgeonRoute.segments]),
    legs: legsMap,
    resources: mergedResources,
    locations: uniqBy([...mainRoute.locations, ...surgeonRoute.locations], 'id'),
  };
};

export const convertCommercialFlights = async (
  trip: RouteActionsType,
  teamResources: Partial<TeamResourcesByKeyword>,
  isTmdxDriveCompatible: boolean,
): Promise<Pick<RoutePlanType, 'segments' | 'legs' | 'locations'>> => {
  const actions = convertActionsToSortedFlatArray(trip.actions);

  const locations = await fetchPlanLocations(actions);

  const commercialRoute = await convertCustomActions(actions, teamResources, isTmdxDriveCompatible);

  return {
    ...commercialRoute,
    locations,
  };
};

export const mergeCommercialWithMainRoute = (
  mainRoute: RoutePlanType,
  commercialPlan: Pick<RoutePlanType, 'segments' | 'legs' | 'locations'>,
): RoutePlanType => {
  if (!commercialPlan?.segments.length) return mainRoute;

  const routeMap = new Map(mainRoute.legs);
  const newSegments = mainRoute.segments.map((segment) => {
    const commercialSegment = commercialPlan.segments.find((s) => {
      const sameStart = s.startLocation.id === segment.startLocation.id;
      const sameEnd = s.endLocation.id === segment.endLocation.id;
      const sameSegmentType = s.type === segment.type;

      return sameStart && sameEnd && sameSegmentType;
    });

    if (!commercialSegment) return segment;

    const commercialLegs = commercialPlan.legs.get(getSegmentMapKey(commercialSegment)) || [];
    const segmentLegs = mainRoute.legs.get(getSegmentMapKey(segment)) || [];
    const fakeCommercialFlight = segmentLegs.find((l) => l.legType === RouteLegTypeEnum.CommercialFlightAviaLeg);
    const realCommercialFlight = commercialLegs.find((l) => l.legType === RouteLegTypeEnum.CommercialFlightAviaLeg);

    if (fakeCommercialFlight && realCommercialFlight) {
      const hasResourceIntersection = !!intersectionBy(
        fakeCommercialFlight.resources,
        realCommercialFlight.resources,
        'id',
      ).length;

      if (hasResourceIntersection) {
        routeMap.delete(getSegmentMapKey(segment));
        routeMap.set(getSegmentMapKey(commercialSegment), commercialLegs);

        return commercialSegment;
      }
    }

    return segment;
  });

  return {
    ...mainRoute,
    segments: newSegments,
    legs: routeMap,
    locations: uniqBy([...mainRoute.locations, ...commercialPlan.locations], 'id'),
  };
};
